import type { ReactNode } from "react";

import "./Nota.css";

interface Props {
  /** A pergunta, na voz de quem está com o dedo sobre o botão. */
  resumo: string;
  children: ReactNode;
  /** Abre já expandida. Use só quando a resposta for pré-requisito do ato. */
  aberta?: boolean;
}

/**
 * Explicação de como a coisa funciona — a pergunta visível, a resposta a um
 * toque.
 *
 * <h2>Por que ela não é um `Alerta`</h2>
 *
 * <p>`Alerta` diz <b>estado</b>: deu erro, falta dado, deu certo. Ele é
 * `role="alert"`/`role="status"`, ou seja, uma região viva — e texto explicativo
 * <b>permanente</b> dentro de uma região viva é ruído para quem usa leitor de
 * tela, porque nada ali muda nunca.
 *
 * <p>O que faltava no sistema era o degrau abaixo: a prosa que ensina o que o
 * botão vai fazer. Ela existia como `Alerta tom="informacao"` na tela de
 * etiquetas, e a conta apareceu na captura de 360 px: <b>282 px</b> de bloco
 * azul para explicar um botão de <b>44 px</b>. A explicação pesava seis vezes
 * mais que a ação que ela explica.
 *
 * <p>Aqui o texto não é cortado — <b>ele é dobrado</b>. O resumo continua
 * sempre na tela, porque é ele que faz a pergunta; a resposta fica atrás de um
 * toque, e o `&lt;details&gt;` nativo cuida de teclado, leitor de tela e do
 * estado aberto sem uma linha de JavaScript.
 *
 * <h2>Nativo de propósito</h2>
 *
 * <p>`&lt;details&gt;`/`&lt;summary&gt;` já é um botão de expansão para a
 * plataforma: recebe foco, responde a Enter e Espaço, anuncia
 * "expandido/recolhido" e continua funcionando com JavaScript quebrado. Um
 * `&lt;div onClick&gt;` com `aria-expanded` seria a reimplementação disso, com
 * os mesmos defeitos que a plataforma já resolveu.
 *
 * <p>⚠️ O `&lt;summary&gt;` tem o piso de <b>44 px</b> de alvo de toque, como
 * todo controle do sistema — ver `docs/frontend/responsividade.md`.
 */
export function Nota({ resumo, children, aberta = false }: Props) {
  return (
    <details className="ui-nota" open={aberta}>
      <summary className="ui-nota__resumo">{resumo}</summary>
      {/* O corpo tem classe própria porque é ele que precisa de `min-width: 0`:
          o texto vem de quem escreveu a tela, mas cita dado do servidor — nome
          de material, código de lote — e item de flex/grid não encolhe abaixo
          do próprio conteúdo. É a causa clássica da rolagem lateral. */}
      <div className="ui-nota__corpo">{children}</div>
    </details>
  );
}
